import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { useAuth } from '../hooks/useAuth';
import { useSip } from '../hooks/useSip';
import { useTranslation } from '../hooks/useTranslation';
import { dateLocale } from '../i18n';
import { now, formatClockTime, formatClockDate } from '../utils/clock';
import { AnalogClock } from './AnalogClock';
import { useStandbyBrightness } from '../hooks/useStandbyBrightness';
import { useEmergencyActive } from '../hooks/useEmergencyActive';
import {
  STANDBY_PALETTES,
  CLOCK_FONT_STACK,
  CLOCK_FONT_SIZES,
  ANALOG_CLOCK_SIZES,
  DATE_FONT_SIZES,
  CLOCK_WEIGHT,
  DATE_WEIGHT,
  clockFontSize,
  fitAnalogClock,
  clockDateOptions,
} from '../constants/standbyStyle';
import { Z_INDEX } from '../constants/zIndex';

const AUTO_SLEEP_TIMEOUT = 60_000; // 1 minute idle → auto-sleep
const PHOTO_INTERVAL = 60_000; // 60s per photo in sleep mode
const IDLE_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll'] as const;

interface SleepPhoto {
  id: number;
  url: string;
  description?: string | null;
}

/**
 * SleepOverlay — Full-screen sleep mode.
 * Laag: `Z_INDEX.SLEEP_OVERLAY` — de hoogste van de stand-byband, maar onder elke
 * melding en onder een oproep. Zie constants/zIndex.ts.
 * Auto-engages after 1 minute of idle as fallback screensaver.
 * When screensaverMode is 'photos', shows a dim photo slideshow with clock overlay.
 * Any touch, click, or keypress wakes the screen.
 */
export function SleepOverlay() {
  const { settings, setSleep } = useDisplaySettings();
  const { api, isAuthenticated } = useAuth();
  const { currentCall } = useSip();
  const { t, lang } = useTranslation();
  const [time, setTime] = useState(now);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const clockStyle = settings.clockStyle;
  const clockSize = settings.clockSize;
  const weight = settings.clockBold ? 'bold' : 'regular';
  const showPhotos = settings.screensaverMode === 'photos';
  const palette = STANDBY_PALETTES[settings.standbyBackground];

  // Echte schermverlichting, wakker en in de slaapstand (APK ≥ 1.0.5).
  useStandbyBrightness(settings.sleepMode, settings.brightness, settings.sleepBrightness);

  // Een noodoproep (aftellen, of de bewoner op de noodpagina) haalt de slaapstand METEEN
  // weg en houdt hem weg — niet pas wanneer de hulpdienst belt.
  const emergency = useEmergencyActive();
  useEffect(() => {
    if (emergency && settings.sleepMode) {
      console.log('[SleepOverlay] Emergency active — waking');
      setSleep(false);
    }
  }, [emergency, settings.sleepMode, setSleep]);

  // Auto-wake when an incoming call arrives (so the pickup screen is visible)
  useEffect(() => {
    if (settings.sleepMode && currentCall && currentCall.state === 'ringing_in') {
      console.log('[SleepOverlay] Incoming call detected — auto-waking');
      setSleep(false);
    }
  }, [settings.sleepMode, currentCall, setSleep]);

  // ── Photo slideshow state ────────────────────────────────
  const [photos, setPhotos] = useState<SleepPhoto[]>([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [photoLoaded, setPhotoLoaded] = useState(false);

  // Fetch photos when entering sleep mode
  useEffect(() => {
    if (!settings.sleepMode || !showPhotos || !isAuthenticated) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<{ success: boolean; data: SleepPhoto[] }>('/api/v1/user/photos/screensaver');
        if (cancelled) return;

        let list: SleepPhoto[] = [];
        if (result?.success && Array.isArray(result.data)) {
          list = result.data;
        } else if (Array.isArray(result)) {
          list = result as unknown as SleepPhoto[];
        } else if (result?.data && Array.isArray(result.data)) {
          list = result.data;
        }

        // Shuffle photos for variety
        for (let i = list.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [list[i], list[j]] = [list[j], list[i]];
        }

        setPhotos(list);
        setPhotoIdx(0);
        setPhotoLoaded(false);
      } catch (err) {
        console.warn('[SleepOverlay] Failed to fetch photos:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [settings.sleepMode, showPhotos, isAuthenticated, api]);

  // Cycle photos
  useEffect(() => {
    if (!settings.sleepMode || !showPhotos || photos.length <= 1) return;
    const id = setInterval(() => {
      setPhotoIdx(prev => (prev + 1) % photos.length);
      setPhotoLoaded(false);
    }, PHOTO_INTERVAL);
    return () => clearInterval(id);
  }, [settings.sleepMode, showPhotos, photos.length]);

  // ── Auto-sleep idle detection ──────────────────────────────
  const resetAutoSleep = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Only auto-engage when the screensaver is ALLOWED for this location
    // (portal display default). When disabled, the device must never auto-sleep
    // — only the manual "Sleep now" button may set sleep mode.
    if (!settings.sleepMode && settings.screensaverEnabled && !emergency) {
      timerRef.current = setTimeout(() => {
        setSleep(true);
      }, AUTO_SLEEP_TIMEOUT);
    }
  }, [settings.sleepMode, settings.screensaverEnabled, emergency, setSleep]);

  useEffect(() => {
    if (settings.sleepMode) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    resetAutoSleep();

    const handler = () => resetAutoSleep();
    for (const event of IDLE_EVENTS) {
      document.addEventListener(event, handler, { passive: true });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of IDLE_EVENTS) {
        document.removeEventListener(event, handler);
      }
    };
  }, [settings.sleepMode, resetAutoSleep]);

  // ── Clock update while sleeping ────────────────────────────
  useEffect(() => {
    if (!settings.sleepMode) return;
    setTime(now());
    const interval = clockStyle === 'analog' ? 1_000 : 30_000;
    const id = setInterval(() => setTime(now()), interval);
    return () => clearInterval(id);
  }, [settings.sleepMode, clockStyle]);

  // ── Wake handler ───────────────────────────────────────────
  const wakingRef = useRef(false);
  const wake = useCallback((e?: React.SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (wakingRef.current) return;
    wakingRef.current = true;
    setTimeout(() => {
      setSleep(false);
      wakingRef.current = false;
    }, 100);
  }, [setSleep]);

  // Listen for keypress to wake
  useEffect(() => {
    if (!settings.sleepMode) return;
    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!wakingRef.current) {
        wakingRef.current = true;
        setTimeout(() => {
          setSleep(false);
          wakingRef.current = false;
        }, 100);
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [settings.sleepMode, setSleep]);

  if (!settings.sleepMode || emergency) return null;

  // Explicit locale: `[]` would follow the WebView's locale, not the resident's
  // app language — see i18n/locale.ts.
  const locale = dateLocale(lang);
  const timeStr = formatClockTime(time, locale);
  const dateStr = formatClockDate(time, locale, clockDateOptions(settings.clockShowYear));
  const currentPhoto = showPhotos && photos.length > 0 ? photos[photoIdx] : null;

  return (
    <div
      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => wake(e)}
      onTouchStart={(e) => wake(e)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.SLEEP_OVERLAY,
        // With a photo the image itself is the background; without one, the lit
        // standby gradient keeps the tablet from looking switched off.
        background: currentPhoto ? '#000000' : palette.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {/* Photo background (dimmed) */}
      {currentPhoto && (
        <>
          <img
            key={currentPhoto.url}
            src={currentPhoto.url}
            alt=""
            onLoad={() => setPhotoLoaded(true)}
            onError={() => {
              // Skip to next photo on error
              if (photos.length > 1) {
                setPhotoIdx(prev => (prev + 1) % photos.length);
                setPhotoLoaded(false);
              }
            }}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: photoLoaded ? 1 : 0,
              transition: 'opacity 2s ease-in-out',
            }}
          />
          {/* Dark overlay so clock is readable */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          }} />
        </>
      )}
      {/* Clock. Over a photo it stays modest (the image carries the screen);
          without a photo it IS the screen, so it gets the full standby
          treatment — large, white and glowing — otherwise residents can't tell
          the tablet apart from a powered-off one. */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        {clockStyle === 'analog' ? (
          <AnalogClock
            size={currentPhoto ? 160 : fitAnalogClock(ANALOG_CLOCK_SIZES[clockSize])}
            bold={settings.clockBold}
            color={currentPhoto ? 'rgba(255,255,255,0.5)' : palette.text}
          />
        ) : (
          <div style={{
            fontFamily: `var(--caro-font-display, ${CLOCK_FONT_STACK})`,
            color: currentPhoto ? 'rgba(255,255,255,0.6)' : palette.text,
            fontSize: currentPhoto ? 'var(--font-heading)' : clockFontSize(CLOCK_FONT_SIZES[clockSize]),
            fontWeight: CLOCK_WEIGHT[weight],
            letterSpacing: '0.02em',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            textShadow: currentPhoto ? '0 2px 12px rgba(0,0,0,0.8)' : palette.clockGlow,
          }}>
            {timeStr}
          </div>
        )}
        <div style={{
          fontFamily: `var(--caro-font-display, ${CLOCK_FONT_STACK})`,
          color: currentPhoto ? 'rgba(255,255,255,0.5)' : palette.textSoft,
          fontSize: currentPhoto ? 'var(--font-body)' : `${DATE_FONT_SIZES[clockSize]}px`,
          fontWeight: DATE_WEIGHT[weight],
          letterSpacing: '0.04em',
          marginTop: '16px',
          textShadow: currentPhoto ? '0 1px 8px rgba(0,0,0,0.8)' : palette.dateGlow,
        }}>
          {dateStr}
        </div>
        <div style={{
          color: currentPhoto ? 'rgba(255,255,255,0.35)' : palette.hint,
          fontSize: 'var(--font-body)',
          fontWeight: 400,
          marginTop: '24px',
          textShadow: currentPhoto ? '0 1px 8px rgba(0,0,0,0.8)' : 'none',
        }}>
          {t.display.tapToWake}
        </div>
      </div>
    </div>
  );
}
